/* eslint-disable react/require-default-props */
import React from 'react';
import {Platform, View} from 'react-native';
import {get, isNull, noop} from 'lodash';

import Avatar from '/component/base/Avatar';
import Icon from '/component/base/Icon';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import {Reports} from '/constant/TestIDs';
import {REPORT_CONFIG} from '/constant/TestIDs/reportTestIds';
import testProperties from '/util/testing/testProperties';

import KeyboardFocusable from '../ExtKeyboard/KeyboardFocusable';

import {styles} from './PersonListItem.style';

interface LoadingData {
  total: number;
}

interface ReportMeta {
  page: {
    total: number;
  };
}

interface PersonListItemProps {
  avatarUrl: string | null;
  avatarEmail: string | null;
  isCoach: boolean;
  name: string | JSX.Element;
  role: string | JSX.Element;
  style: Object;
  attribute: JSX.Element | null;
  readonly: boolean;
  reportName: string;
  onPress: () => void;
  hasChevron?: boolean;
  isCenterAlignedEvenlySpaced: boolean;
  newLabel: JSX.Element | null;
  hideAvatar: boolean;
  acceptBy: JSX.Element | null;
  acceptByIcon: JSX.Element | null;
  isCABReport: boolean;
  isCSBReport: boolean;
  bonusIndicator: JSX.Element | null;
  CandidateDispositionInfo: JSX.Element | null;
  fullName: string | null;
  isCandidateReport: boolean;
  onFocus: () => void;
  index: number;
  loadingData: LoadingData | null;
  reportMeta: ReportMeta | null;
  accessibilityLabel?: string | null;
  accessibilityHint?: string | null;
  totalCount: number;
  renderNameElementDirectly?: boolean;
  renderRoleElementDirectly?: boolean;
  allowFontScaling?: boolean;
  maxFontSizeMultiplier?: number;
}
export const PersonListItem: React.FC<PersonListItemProps> = ({
  avatarUrl = null,
  avatarEmail = null,
  isCoach = true,
  name = '',
  role = '',
  style: styleProp = {},
  attribute = null,
  readonly = false,
  reportName = '',
  onPress = noop,
  hasChevron = true,
  isCenterAlignedEvenlySpaced = false,
  newLabel = null,
  hideAvatar = false,
  acceptBy = null,
  acceptByIcon = null,
  isCABReport = false,
  bonusIndicator,
  CandidateDispositionInfo = null,
  isCSBReport = false,
  fullName = '',
  isCandidateReport = false,
  onFocus = noop,
  index = 0,
  loadingData = null,
  reportMeta = null,
  accessibilityLabel: accessibilityLabelOverride = null,
  accessibilityHint: accessibilityHintOverride = null,
  totalCount = 0,
  renderNameElementDirectly = false,
  renderRoleElementDirectly = false,
  allowFontScaling = false,
  maxFontSizeMultiplier = undefined,
}: PersonListItemProps) => {
  let total = get(reportMeta, 'page.total', get(loadingData, 'total', ''));
  const totalNumber = Number(total) || 0;
  const accessibilityLabel =
    accessibilityLabelOverride ||
    (Number.isFinite(index) && index >= 0 && totalNumber > 0
      ? `Open ${fullName} profile: ${index + 1} of ${totalNumber}`
      : totalCount > 0
      ? `Open ${fullName} profile: ${index + 1} of ${totalCount}`
      : `Open ${fullName} profile`);
  const accessibilityHint =
    accessibilityHintOverride || 'Double tap to open the details';

  const config = REPORT_CONFIG[reportName];

  const nameTestId = config?.isCoach
    ? Reports.reportCoachName
    : Reports.reportClientFullName;

  const roleTestID = config?.roleTestId ?? Reports.reportCandidateDateAdded;

  return (
    <ListItem
      readonly={readonly}
      onPress={onPress}
      onFocus={onFocus}
      isCandidateReport={isCandidateReport}
      accessible={false}
      focusable={false}
      label={
        <KeyboardFocusable
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          accessibilityHint={accessibilityHint}
          onFocus={onFocus}
          disableFocusRing={Platform.OS === 'android'}
          onPress={onPress}
          focusRingStyle={styles.listItem}>
          <View
            style={[
              styles.base,
              styleProp.base,
              {
                flexDirection: 'row',
                alignItems: 'center',
                paddingRight: 10,
              },
            ]}>
            {!hideAvatar && (
              <Avatar
                isCoach={isCoach}
                avatarUrl={avatarUrl}
                avatarKey={avatarEmail}
                fullName={fullName}
              />
            )}
            <View
              style={{...styles.textWrapper, ...(styleProp.textWrapper || {})}}>
              <View
                style={
                  isCSBReport ? styles.nameContainerCSB : styles.nameContainer
                }>
                {renderNameElementDirectly && typeof name !== 'string' ? (
                  <View {...testProperties(nameTestId)} accessible={false}>
                    {name}
                  </View>
                ) : (
                  <Text
                    variant="button"
                    allowFontScaling={allowFontScaling}
                    maxFontSizeMultiplier={maxFontSizeMultiplier}
                    {...testProperties(nameTestId)}
                    style={
                      isCenterAlignedEvenlySpaced && styles.clientFullNameText
                    }
                    accessible={true}
                    accessibilityLabel={
                      typeof name === 'string' ? name : undefined
                    }>
                    {name}
                  </Text>
                )}
                {!isNull(newLabel) && newLabel}
                {!isNull(acceptByIcon) && acceptByIcon}
              </View>
              {!isNull(role) && (
                <View
                  accessible={false}
                  style={[styles.roleContainer, styleProp.role || {}]}>
                  {renderRoleElementDirectly && typeof role !== 'string' ? (
                    <View {...testProperties(roleTestID)} accessible={false}>
                      {role}
                    </View>
                  ) : (
                    <Text
                      emphasis="standard"
                      allowFontScaling={allowFontScaling}
                      maxFontSizeMultiplier={maxFontSizeMultiplier}
                      testID={roleTestID}
                      style={[
                        isCenterAlignedEvenlySpaced && styles.clientRoleText,
                        {flexShrink: 1},
                      ]}
                      accessible={false}
                      accessibilityLabel={
                        typeof role === 'string' ? role : undefined
                      }>
                      {role}
                    </Text>
                  )}
                </View>
              )}
              {!isNull(attribute) && (
                <View
                  style={
                    isCenterAlignedEvenlySpaced
                      ? styles.peopleToContactAttributeText
                      : !isCABReport
                      ? isCSBReport
                        ? styles.attributeCSB
                        : styles.attribute
                      : null
                  }
                  accessible={true}
                  accessibilityLabel={
                    typeof attribute === 'string' ? attribute : undefined
                  }>
                  {attribute}
                </View>
              )}
              {!isNull(acceptBy) && <View>{acceptBy}</View>}
              {!isNull(CandidateDispositionInfo) && (
                <View>{CandidateDispositionInfo}</View>
              )}
              {!isNull(bonusIndicator) && (
                <View style={styleProp.bonusIndicator || {}}>
                  {bonusIndicator}
                </View>
              )}
            </View>

            {hasChevron && (
              <View style={{marginLeft: 12}}>
                <Icon
                  name="chevron-right"
                  style={styles.arrowIcon}
                  accessible={false}
                />
              </View>
            )}
          </View>
        </KeyboardFocusable>
      }
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      style={
        !isCenterAlignedEvenlySpaced
          ? {
              base: isCSBReport ? {paddingVertical: 10} : {paddingVertical: 20},
            }
          : {}
      }
      // suffix={
      //   hasChevron && (
      //     <Icon
      //       name={'chevron-right'}
      //       style={styles.arrowIcon}
      //       accessible={false}
      //     />
      //   )
      // }
    />
  );
};

export default PersonListItem;
